import {compareText, normalizeText} from './normalizeText';

// Valor del filtro de cliente que muestra todo.
export const ALL_CLIENTS = 'all';

// La clave del grupo de los prompts de toda la agencia. Los de un cliente usan `client-<id>`.
export const AGENCY_GROUP_KEY = 'all';

/**
 * ¿El prompt tiene TODAS las palabras buscadas en su nombre, su «qué vas a obtener» o su texto?
 * Sin acentos ni mayúsculas, y por subcadena: «reunion» encuentra «Reuniones».
 */
export function matchesSearch(prompt, query) {
    const words = normalizeText(query).split(' ').filter(Boolean);
    if (words.length === 0) return true;
    const haystack = normalizeText([prompt.title, prompt.value_preview, prompt.prompt_text].join(' '));
    return words.every((word) => haystack.includes(word));
}

/**
 * El filtro de cliente muestra lo de ESE cliente más lo de toda la agencia: es lo que la IA tiene a
 * mano cuando se le habla de ese cliente.
 */
export function matchesClient(prompt, clientId) {
    if (clientId === null || clientId === undefined || clientId === ALL_CLIENTS || clientId === '') return true;
    return !prompt.client || String(prompt.client.id) === String(clientId);
}

function byTitle(a, b) {
    return compareText(a.title, b.title) || (a.id - b.id);
}

/**
 * La lista propia, agrupada por alcance: primero «Para todos los clientes», después un grupo por
 * cliente ordenado por nombre (sin acentos ni mayúsculas) y los clientes inactivos al final.
 *
 * El nombre y el estado del cliente salen de `clients` (la lista del endpoint); si un prompt apunta
 * a un cliente que no está ahí, se usa el nombre que trae el propio prompt y se lo trata como activo.
 * Los grupos sin prompts después de filtrar no se devuelven.
 */
export function groupOwnPrompts(own, clients, {search = '', clientId = ALL_CLIENTS} = {}) {
    const clientsById = new Map((clients || []).map((client) => [String(client.id), client]));
    const visible = (own || []).filter((prompt) => matchesClient(prompt, clientId) && matchesSearch(prompt, search));

    const groups = [];
    const agencyWide = visible.filter((prompt) => !prompt.client);
    if (agencyWide.length > 0) {
        groups.push({key: AGENCY_GROUP_KEY, client: null, prompts: agencyWide.sort(byTitle)});
    }

    const perClient = new Map();
    visible.filter((prompt) => prompt.client).forEach((prompt) => {
        const id = String(prompt.client.id);
        if (!perClient.has(id)) {
            const known = clientsById.get(id);
            perClient.set(id, {
                key: `client-${id}`,
                client: {
                    id: prompt.client.id,
                    name: known ? known.name : prompt.client.name,
                    isActive: known ? known.is_active !== false : true,
                },
                prompts: [],
            });
        }
        perClient.get(id).prompts.push(prompt);
    });

    const clientGroups = [...perClient.values()];
    clientGroups.forEach((group) => group.prompts.sort(byTitle));
    clientGroups.sort((a, b) => {
        if (a.client.isActive !== b.client.isActive) return a.client.isActive ? -1 : 1;
        return compareText(a.client.name, b.client.name) || (a.client.id - b.client.id);
    });
    return groups.concat(clientGroups);
}

/**
 * Los clientes del filtro y del selector del editor: los activos primero y después los inactivos,
 * cada tramo por nombre.
 */
export function sortClients(clients) {
    return [...(clients || [])].sort((a, b) => {
        const aActive = a.is_active !== false;
        const bActive = b.is_active !== false;
        if (aActive !== bActive) return aActive ? -1 : 1;
        return compareText(a.name, b.name) || (a.id - b.id);
    });
}
